'use client';
import { useEffect, useState } from 'react';

/**
 * Custom hook to check if a given media query matches the current viewport.
 *
 * @remarks
 * This hook is server-side rendering (SSR) friendly. It returns `undefined` on the server side.
 *
 * @param query - The media query to be evaluated.
 * @returns A boolean value indicating whether the media query matches the current viewport or `undefined` on the server side.
 */
export const useMediaQuery = (query: string): boolean | undefined => {
  const [matches, setMatches] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const onMediaQueryChange = () => {
      setMatches(mediaQuery.matches);
    };

    onMediaQueryChange();
    // Check if addEventListener is available, otherwise use addListener
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onMediaQueryChange);
    } else {
      mediaQuery.addListener(onMediaQueryChange);
    }

    return () => {
      // Check if removeEventListener is available, otherwise use removeListener
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', onMediaQueryChange);
      } else {
        mediaQuery.removeListener(onMediaQueryChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return matches;
};
